import { useCallback, useEffect, useState } from 'react';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import type { CaptureSession } from '@/shared/capture/capture-session';

interface MeterSessionsState {
  deletingSessionId: string | null;
  error: string | null;
  isLoading: boolean;
  sessions: CaptureSession[];
}

export function useInventoryMeterSessions(input: { locationId?: number | string; limit?: number } = {}) {
  const { captureSessions } = useInventoryAppServices();
  const [state, setState] = useState<MeterSessionsState>({
    deletingSessionId: null,
    error: null,
    isLoading: true,
    sessions: [],
  });

  const refresh = useCallback(async () => {
    setState(current => ({ ...current, error: null, isLoading: true }));

    try {
      const sessions = await captureSessions.listInventorizationSessions();
      const visibleSessions = sessions.filter(
        session => typeof input.locationId === 'undefined' || session.locationId === input.locationId,
      );

      setState({
        deletingSessionId: null,
        error: null,
        isLoading: false,
        sessions: typeof input.limit === 'number' ? visibleSessions.slice(0, input.limit) : visibleSessions,
      });
    } catch (error) {
      setState({
        deletingSessionId: null,
        error: error instanceof Error ? error.message : 'Unable to load local meter sessions.',
        isLoading: false,
        sessions: [],
      });
    }
  }, [captureSessions, input.limit, input.locationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const deleteLocalDraft = useCallback(
    async (sessionId: string) => {
      setState(current => ({ ...current, deletingSessionId: sessionId, error: null }));

      try {
        await captureSessions.deleteLocalDraft(sessionId);
        setState(current => ({
          ...current,
          deletingSessionId: null,
          sessions: current.sessions.filter(session => session.id !== sessionId),
        }));
      } catch (error) {
        setState(current => ({
          ...current,
          deletingSessionId: null,
          error: error instanceof Error ? error.message : 'Unable to delete local draft.',
        }));
      }
    },
    [captureSessions],
  );

  return {
    ...state,
    deleteLocalDraft,
    refresh,
  };
}
